import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { uploadFile, deleteFile, storageBuckets } from '../../lib/supabase'
import { dateStr, uid } from '../../utils/formatters'
import { SkeletonList } from '../ui/Skeleton'

const DOC_TYPES = [
  { value: 'semua', label: '📁 Semua' },
  { value: 'foto', label: '📸 Foto' },
  { value: 'kontrak', label: '📄 Kontrak' },
  { value: 'gambar', label: '📐 Gambar' },
  { value: 'berita-acara', label: '📝 BA' },
  { value: 'lainnya', label: '📎 Lain' },
]

export default function Documents() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [documents, setDocuments] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [filterProject, setFilterProject] = useState('all')
  const [filterType, setFilterType] = useState('semua')
  const [lightbox, setLightbox] = useState(null)
  const [uploading, setUploading] = useState(false)

  const blank = { project_id: '', name: '', type: 'foto', date: new Date().toISOString().split('T')[0], file_path: '', file_url: '', notes: '' }
  const [form, setForm] = useState(blank)
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => { if (!user) return; fetchData() }, [user])

  const fetchData = async () => {
    setLoading(true)
    const [{ data: docs }, { data: proj }] = await Promise.all([
      supabase.from('documents').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('projects').select('*').eq('user_id', user.id),
    ])
    if (docs) setDocuments(docs)
    if (proj) setProjects(proj)
    setLoading(false)
  }

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setForm(p => ({ ...p, name: p.name || file.name.replace(/\.[^.]+$/, '') }))
    }
  }

  const save = async () => {
    if (!form.name) { toast.show('Nama dokumen wajib diisi', 'error'); return }
    let filePath = form.file_path
    let fileUrl = form.file_url
    if (selectedFile) {
      setUploading(true)
      try {
        const path = `${user.id}/${uid()}-${selectedFile.name}`
        const result = await uploadFile(storageBuckets.documents, selectedFile, path)
        filePath = result.path; fileUrl = result.url
      } catch {
        toast.show('Gagal upload file', 'error')
        setUploading(false); return
      }
      setUploading(false)
    }
    await supabase.from('documents').insert([{ ...form, file_path: filePath, file_url: fileUrl, user_id: user.id, id: uid() }])
    setShow(false); setSelectedFile(null)
    toast.show('Dokumen disimpan')
    fetchData()
  }

  const del = async (id, name, filePath) => {
    const ok = await confirm(`Hapus dokumen "${name}"?`, { title: 'Hapus Dokumen', icon: '📁', confirmLabel: 'Hapus', confirmVariant: 'danger' })
    if (!ok) return
    try {
      if (filePath) await deleteFile(storageBuckets.documents, filePath)
      await supabase.from('documents').delete().eq('id', id)
      toast.show('Dokumen dihapus')
      fetchData()
    } catch { toast.show('Gagal menghapus', 'error') }
  }

  const typeIcons = { foto: '📸', kontrak: '📄', gambar: '📐', 'berita-acara': '📝', lainnya: '📎' }
  const isImage = (url) => url && /\.(jpg|jpeg|png|gif|webp)$/i.test(url)

  const filtered = documents.filter(d => {
    const matchP = filterProject === 'all' || d.project_id === filterProject
    const matchT = filterType === 'semua' || d.type === filterType
    return matchP && matchT
  })

  if (loading) return <SkeletonList count={6} />

  return (
    <div>
      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Preview" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Filters */}
      <div className="filter-row">
        <select className="form-select" style={{ width: 180 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="all">Semua Proyek</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button onClick={() => { setForm(blank); setSelectedFile(null); setShow(true) }}>➕ Upload</Button>
      </div>

      {/* Type chips */}
      <div className="filter-row" style={{ gap: 6, marginBottom: 14 }}>
        {DOC_TYPES.map(t => (
          <button key={t.value} className={`doc-type-chip${filterType === t.value ? ' active' : ''}`} onClick={() => setFilterType(t.value)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Stats row */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        {filtered.length} dari {documents.length} dokumen
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📁</div>
          <p>{filterType !== 'semua' || filterProject !== 'all' ? 'Tidak ada dokumen yang cocok' : 'Belum ada dokumen'}</p>
          <Button variant="primary" style={{ marginTop: 12 }} onClick={() => { setForm(blank); setSelectedFile(null); setShow(true) }}>
            ➕ Upload Dokumen Pertama
          </Button>
        </div>
      ) : (
        <div className="doc-grid">
          {filtered.map(d => {
            const proj = projects.find(p => p.id === d.project_id)
            const imgUrl = isImage(d.file_url) ? d.file_url : null
            return (
              <div key={d.id} className="doc-card">
                {imgUrl ? (
                  <img
                    className="doc-thumb"
                    src={imgUrl}
                    alt={d.name}
                    onClick={() => setLightbox(imgUrl)}
                    style={{ cursor: 'zoom-in' }}
                  />
                ) : (
                  <div className="doc-thumb-placeholder">{typeIcons[d.type] || '📎'}</div>
                )}
                <div className="doc-info">
                  <div className="doc-name" title={d.name}>{d.name}</div>
                  {proj && <div style={{ fontSize: 9, color: 'var(--accent)', marginTop: 2 }}>{proj.name}</div>}
                  <div className="doc-meta">{dateStr(d.created_at || d.date)}</div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {d.file_url && (
                      <a href={d.file_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-xs">↗</a>
                    )}
                    <button className="btn btn-danger btn-xs" style={{ flex: 1 }} onClick={() => del(d.id, d.name, d.file_path)}>🗑️</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        show={show}
        onClose={() => { setShow(false); setSelectedFile(null) }}
        title="📎 Upload Dokumen"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShow(false); setSelectedFile(null) }}>Batal</Button>
            <Button onClick={save} disabled={uploading}>{uploading ? '🔄 Uploading...' : '💾 Simpan'}</Button>
          </>
        }
      >
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Proyek</label>
            <select className="form-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
              <option value="">Pilih Proyek</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tipe</label>
            <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {DOC_TYPES.filter(t => t.value !== 'semua').map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Nama Dokumen *</label>
          <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nama file / dokumen" />
        </div>
        <div className="form-group">
          <label className="form-label">File (Foto / PDF)</label>
          <input type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display: 'none' }} id="doc-upload" />
          <label htmlFor="doc-upload" className="photo-upload-zone">
            {selectedFile ? `✓ ${selectedFile.name}` : '📁 Klik untuk pilih file'}
          </label>
        </div>
        <div className="form-group">
          <label className="form-label">Catatan</label>
          <textarea className="form-textarea" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Catatan opsional..." style={{ minHeight: 60 }} />
        </div>
      </Modal>
    </div>
  )
}